/**
 * Rebar Cover Tool
 * 
 * Manages rebar cover settings for structural elements.
 */

import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';

export class RebarCoverTool extends BaseTool {
    id = 'rebar_cover';
    name = 'Rebar Cover';
    cursor = 'crosshair';

    private availableCovers = [25, 40, 50, 75]; // mm

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        // Find intersection with face index — manual scene raycast, since BIM
        // element meshes carry no R3F handlers and event.intersections only
        // ever contains the event-catching plane.
        const hit = this.raycastSceneAtEvent(event).find(i => {
            const id = this.findId(i.object);
            if (!id) return false;
            const el = this.context!.bimStore.project.elements.get(id);
            return el && el.rebarHostData; // Must be a valid host
        });

        if (hit && hit.faceIndex !== undefined) {
            const id = this.findId(hit.object)!;
            const element = this.context.bimStore.project.elements.get(id);

            if (element && element.rebarHostData) {
                // Cycle cover for this face. Replace-on-write via updateElement
                // so subscribers re-render and undo captures the change.
                const nextCover = this.nextCover(element.rebarHostData, hit.faceIndex);
                this.context.bimStore.updateElement(id, {
                    rebarHostData: {
                        ...element.rebarHostData,
                        faceCovers: {
                            ...(element.rebarHostData.faceCovers ?? {}),
                            [hit.faceIndex]: nextCover,
                        },
                    },
                });

                // Update preview
                this.context.appStore.setToolPreview({
                    type: 'rebar_cover',
                    elementId: id,
                    highlightFaceIndex: hit.faceIndex,
                    position: hit.point.toArray(),
                    coverValue: nextCover,
                });
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const hit = this.raycastSceneAtEvent(event).find(i => {
            const id = this.findId(i.object);
            if (!id) return false;
            const el = this.context!.bimStore.project.elements.get(id);
            return el && el.rebarHostData;
        });

        if (hit && hit.faceIndex !== undefined) {
            const id = this.findId(hit.object)!;
            this.context.appStore.setToolPreview({
                type: 'rebar_cover_hover',
                elementId: id,
                highlightFaceIndex: hit.faceIndex,
                position: hit.point.toArray(),
                color: '#00ff00',
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    /** Compute (without persisting) the next cover value for a face. */
    private nextCover(hostData: { faceCovers?: Record<string, number> }, faceIndex: number): number {
        const currentCover = hostData.faceCovers?.[faceIndex] || 25;
        const currentIndex = this.availableCovers.indexOf(currentCover);
        const nextIndex = (currentIndex + 1) % this.availableCovers.length;
        return this.availableCovers[nextIndex];
    }
}
